/**
 * Cliente HTTP del frontend hacia el backend Spring.
 *
 * Responsabilidades:
 *   - Adjuntar el JWT en cada request (refrescándolo si está por expirar)
 *   - Serializar el body como JSON
 *   - Convertir errores HTTP en excepciones ApiError tipadas
 *   - Permitir cancelar requests vía AbortController
 *
 * Todas las funciones exportadas reciben `getToken` como primer argumento
 * (la función que viene del useAuth() del frontend). Lo pasamos explícito en
 * lugar de importar keycloak directamente para mantener este módulo
 * "tonto" — pura plumbing de HTTP, sin acoplamiento al provider de auth.
 */

const BASE_URL = import.meta.env.VITE_BACKEND_URL;

/**
 * Excepción tipada para errores del backend.
 * `status` permite al UI decidir según el código (401 → re-login, 429 → "espera").
 */
export class ApiError extends Error {
  constructor(status, message, body) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body; // payload crudo del backend (puede ser objeto o string)
  }
}

/**
 * Helper genérico. Todas las funciones exportadas son wrappers chicos
 * sobre esta función — concentra todo el trabajo de HTTP, auth y errores.
 *
 * @param {string} path          Path del endpoint (ej. "/api/v1/research/discovery")
 * @param {object|null} body     Cuerpo JSON o null para GET
 * @param {() => Promise<string>} getToken
 *                               Función que devuelve un JWT fresco (de useAuth)
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]   Para cancelar requests en curso
 * @param {string} [options.method]         "POST" por default si hay body, "GET" si no
 * @returns {Promise<any>}        Respuesta parseada como JSON
 * @throws {ApiError}             Si el backend devuelve status >= 400
 */
async function request(path, body, getToken, options = {}) {
  // 1) Refrescar el token antes de mandarlo. Si está a <30s de expirar,
  //    keycloak-js lo refresca silenciosamente. Si no se puede refrescar
  //    (sesión SSO vencida), lanza error y el caller debe re-loguear.
  const token = await getToken();

  // 2) Decidir el método HTTP. Si hay body, asumimos POST salvo override.
  const method = options.method || (body == null ? "GET" : "POST");

  // 3) Armar la request.
  const init = {
    method,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    signal: options.signal,
  };
  if (body != null) {
    init.headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }

  // 4) Ejecutar el fetch.
  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, init);
  } catch (netErr) {
    // fetch() solo rechaza por errores de red (DNS, sin conexión, CORS preflight
    // fallido). Errores HTTP (4xx/5xx) NO lanzan acá, hay que checar res.ok.
    throw new ApiError(0, `Network error: ${netErr.message}`, null);
  }

  // 5) Si el HTTP status es de error, parsear el body y lanzar.
  if (!res.ok) {
    let errorBody = null;
    try {
      // El backend Spring devuelve JSON en errores (ej. {"error":"...", "message":"..."})
      errorBody = await res.json();
    } catch {
      // Si no es JSON parseable, agarra el texto crudo.
      errorBody = await res.text().catch(() => null);
    }
    const message =
      errorBody?.message || errorBody?.error || `HTTP ${res.status}`;
    throw new ApiError(res.status, message, errorBody);
  }

  // 6) Parsear la respuesta exitosa como JSON y devolverla.
  return res.json();
}

// ============================================================
// Endpoints de /api/v1/research
// ============================================================

/**
 * Paso 1: descubrimiento de categorías.
 * @param {() => Promise<string>} getToken
 * @param {{query?: string, useContext?: string, refinement?: string,
 *          history?: Array<{role:string, content:any}>}} body
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<{markdown:string, toolOutput:any, usage:object,
 *                    model:string, stopReason:string}>}
 */
export function discovery(getToken, body, options) {
  return request("/api/v1/research/discovery", body, getToken, options);
}

/** Paso 2: especificaciones del producto confirmado. */
export function specs(getToken, body, options) {
  return request("/api/v1/research/specs", body, getToken, options);
}

/** Paso 3: opciones concretas con requirements + budget. */
export function options(getToken, body, options) {
  return request("/api/v1/research/options", body, getToken, options);
}

/** Paso 4: comparación de las opciones marcadas. */
export function compare(getToken, body, options) {
  return request("/api/v1/research/compare", body, getToken, options);
}

/** Paso 5: dónde comprar (puede incluir web search). */
export function whereToBuy(getToken, body, options) {
  return request("/api/v1/research/where-to-buy", body, getToken, options);
}

// ============================================================
// Endpoint de uso (cost tracking del usuario logueado)
// ============================================================

/**
 * Resumen propio: tokens y costo de hoy / mes actual / all-time.
 * GET sin body. Lo consume el UsageBadge en el header.
 * @returns {Promise<{userId:string, today:object, thisMonth:object, allTime:object}>}
 */
export function getUsageSummary(getToken, options) {
  return request("/api/v1/usage", null, getToken, options);
}
